import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Typography,
  Divider,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress,
  Autocomplete,
  IconButton,
  Icon,
} from "@mui/material";
import { callAPI } from "../../../../functions/api";
import { useStore } from "@xyflow/react";
import { v4 as uuidv4 } from "uuid";
import { set } from "lodash";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

const operators = [
  { value: "eq", label: "Equals" },
  { value: "neq", label: "Not Equals" },
  { value: "lt", label: "Less Than" },
  { value: "lte", label: "Less Than or Equal" },
  { value: "gt", label: "Greater Than" },
  { value: "gte", label: "Greater Than or Equal" },
  { value: "contains", label: "Contains" },
];

const logicalOptions = [
  { value: "AND", label: "AND" },
  { value: "OR", label: "OR" },
];

export const FilterNodeModal = ({ open, node }) => {
  const nodeData = node?.data || {};
  const [fieldsToInclude, setFieldsToInclude] = useState([]);
  const [filterClauses, setFilterClauses] = useState([]);

  // const [selectedSchema, setSelectedSchema] = useState("");
  // const [selectedModel, setSelectedModel] = useState("");
  const [error, setError] = useState(null);
  const initializedRef = useRef(false);
  const lastInitializedNodeId = useRef(null);
  const nodes = useStore((s) => s.nodes);
  const edges = useStore((s) => s.edges);

  const upstreamModel = useMemo(() => {
    const incoming = edges.find((e) => e.target === node.id);
    if (!incoming) return null;

    const sourceNode = nodes.find((n) => n.id === incoming.source);
    if (!sourceNode) return null;

    // Extract the schema from the upstream node's output
    const schema = sourceNode.data?.output?.selectedSchema;
    return schema?.properties ? schema : null;
  }, [nodes, edges, node.id]);

  // Set selected model and schema based on upstream model
  // useEffect(() => {
  //   const incoming = edges.find((e) => e.target === node.id);
  //   if (incoming) {
  //     const sourceNode = nodes.find((n) => n.id === incoming.source);
  //     if (sourceNode) {
  //       setSelectedModel(sourceNode.data?.output ? sourceNode.data.output : "");
  //       setSelectedSchema(sourceNode.data?.output?.selectedSchema || "");
  //     }
  //   } else {
  //     setSelectedModel("");
  //     setSelectedSchema("");
  //   }
  // }, [nodes, edges, node.id]);

  const modelFields = useMemo(() => {
    if (!upstreamModel?.properties) return [];
    return Object.entries(upstreamModel.properties).map(([key, val]) => ({
      key,
      label: val.label || key,
    }));
  }, [upstreamModel]);

  useEffect(() => {
    console.log("------------------node", node);
    // Only initialize when modal opens or when switching to a different node
    if (open && (!initializedRef.current || lastInitializedNodeId.current !== node?.id)) {
      const modelSettings = nodeData?.output?.settings || {};
      const savedFields = modelSettings.fieldsToInclude || [];
      // If no fields are saved, default to "ALL"
      setFieldsToInclude(savedFields.length > 0 ? savedFields : ["ALL"]);
      setFilterClauses((modelSettings.filterClauses || []).map((c) => ({ ...c, id: uuidv4() })));
      setError(null);
      initializedRef.current = true;
      lastInitializedNodeId.current = node?.id;
    }

    // Reset initialization flag when modal closes
    if (!open) {
      initializedRef.current = false;
      lastInitializedNodeId.current = null;
    }
  }, [open, node?.id]);

  const addFilterClause = () => {
    setFilterClauses((prev) => [
      ...prev,
      { id: uuidv4(), field: "", operator: "eq", value: "", logic: "AND" },
    ]);
  };

  const removeFilterClause = (id) => {
    setFilterClauses((prev) => prev.filter((c) => c.id !== id));
  };

  const updateFilterClause = (id, key, value) => {
    setFilterClauses((prev) =>
      prev.map((clause) => (clause.id === id ? { ...clause, [key]: value } : clause))
    );
  };

  const onFieldsChange = (event, value) => {
    const ALL_OPTION = "ALL";

    // If "ALL" is selected
    if (value.includes(ALL_OPTION)) {
      // If "ALL" was just added (wasn't in previous selection)
      if (!fieldsToInclude.includes(ALL_OPTION)) {
        setFieldsToInclude([ALL_OPTION]); // Only "ALL"
      } else {
        // If "ALL" was already selected and other fields are being added, remove "ALL"
        const withoutAll = value.filter((v) => v !== ALL_OPTION);
        setFieldsToInclude(withoutAll);
      }
    } else {
      // No "ALL" in selection, use the value as-is
      setFieldsToInclude(value);
    }
  };

  // Persist settings to node.data.output
  useEffect(() => {
    if (!node?.data?.setData) return;

    // Ensure filterClauses is always an array (even if empty)
    const validFilterClauses = filterClauses.filter(
      (clause) => clause.field && clause.operator && clause.value !== ""
    );

    // Expand "ALL" to actual field names for output
    let expandedFieldsToInclude = fieldsToInclude || [];
    if (fieldsToInclude.includes("ALL")) {
      expandedFieldsToInclude = modelFields.map((f) => f.key);
    }

    const settings = {
      fieldsToInclude: expandedFieldsToInclude,
      filterClauses: validFilterClauses.map(({ id, ...rest }) => rest), // exclude `id`
    };

    // console.log("FilterNode: Persisting settings", settings);

    node.data.setData((oldData) => ({
      ...oldData,
      output: {
        ...oldData.output,
        settings,
      },
    }));
  }, [fieldsToInclude, filterClauses]);

  return (
    <Box>
      {modelFields.length === 0 ? (
        <Typography variant="subtitle1" color="orange">
          ⚠ No model metadata found. Connect this node to a model provider.
        </Typography>
      ) : (
        <>
          <Typography sx={{ fontSize: "14px", marginBottom: "5px" }}>
            Select Fields to Include
          </Typography>
          {/* Validation Status */}
          <Box sx={{ mb: 2, p: 1, borderRadius: 1, backgroundColor: "#2a2a2a" }}>
            {fieldsToInclude.length == 0 && (
              <Typography sx={{ fontSize: "12px", color: "#ff9800" }}>
                ⚠ Select at least one field to include
              </Typography>
            )}
            <Typography sx={{ fontSize: "11px", color: "#888", mt: 0.5 }}>
              This node will return all rows with the selected fields. Add filters below to limit
              results.
            </Typography>
          </Box>
          <Autocomplete
            multiple
            options={["ALL", ...modelFields.map((f) => f.key)]}
            value={fieldsToInclude}
            onChange={onFieldsChange}
            renderOption={(props, option) => (
              <li {...props}>{option === "ALL" ? "🔘 All Fields" : option}</li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select fields..."
                variant="outlined"
                sx={{
                  height: "40px",
                  "& .MuiInputBase-root": {
                    minHeight: "30px",
                    padding: "5px 6px",
                    backgroundColor: "#2E2E2E",
                    fontSize: "12px",
                    alignItems: "center",
                  },
                  "& .MuiInputBase-root:focus": {
                    minHeight: "30px",
                    padding: "5px 6px",
                    backgroundColor: "#2E2E2E",
                    fontSize: "12px",
                    alignItems: "center",
                    borderColor: "red",
                  },
                  "& .MuiInputBase-input": {
                    padding: 0,
                    margin: 0,
                    fontSize: "12px",
                    lineHeight: "20px",
                  },
                  "& .MuiChip-root": {
                    height: "24px",
                    fontSize: "12px",
                    margin: "2px",
                  },
                }}
              />
            )}
            sx={{
              minWidth: 250,
              "& .MuiAutocomplete-inputRoot": {
                padding: 0,
              },
              "& .MuiAutocomplete-tag": {
                margin: "2px",
              },
            }}
          />{" "}
          <Divider sx={{ mb: 2, mt: 2, borderColor: "#444" }} />
          <Typography sx={{ fontSize: "14px", marginBottom: "5px" }}>
            Filter Query (Optional)
          </Typography>
          {filterClauses.length === 0 && (
            <Typography sx={{ fontSize: "12px", color: "#888", mb: 2, fontStyle: "italic" }}>
              No filters applied - all data will be included
            </Typography>
          )}
          {filterClauses.map((clause, idx) => (
            <Box
              key={clause.id}
              sx={{
                display: "flex",
                mb: 1,
                flex: 1,
                width: "100%",
                flexDirection: "column",
                gap: 1,
              }}
            >
              {idx > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    flex: 1,
                    width: "100%",
                    flexDirection: "row",
                    gap: 2,
                    marginTop: "15px",
                    marginBottom: "15px",
                  }}
                >
                  <FormControl size="small" sx={{ minWidth: 80 }}>
                    <Select
                      sx={{
                        backgroundColor: "#2E2E2E",
                        fontSize: "12px",
                        height: "30px",
                      }}
                      value={clause.logic || "AND"}
                      onChange={(e) => updateFilterClause(clause.id, "logic", e.target.value)}
                    >
                      {logicalOptions.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )}
              <Box sx={{ display: "flex", flex: 1, width: "100%", gap: 1 }}>
                <FormControl size="small" sx={{ display: "flex", flex: 1 }}>
                  <Select
                    displayEmpty
                    value={clause.field}
                    onChange={(e) => updateFilterClause(clause.id, "field", e.target.value)}
                    sx={{
                      backgroundColor: "#2E2E2E",
                      fontSize: "12px",
                      height: "30px",
                      color: "#fff",

                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#444",
                      },
                    }}
                    renderValue={(selected) =>
                      selected
                        ? modelFields.find((f) => f.key === selected)?.label
                        : "Select field..."
                    }
                  >
                    {modelFields.map((field) => (
                      <MenuItem key={field.key} value={field.key}>
                        {field.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ display: "flex", flex: 1 }}>
                  <Select
                    displayEmpty
                    value={clause.operator}
                    onChange={(e) => updateFilterClause(clause.id, "operator", e.target.value)}
                    sx={{
                      backgroundColor: "#2E2E2E",
                      fontSize: "12px",
                      height: "30px",
                      color: "#fff",

                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#444",
                      },
                    }}
                    renderValue={(selected) =>
                      selected
                        ? operators.find((op) => op.value === selected)?.label.toUpperCase()
                        : "Select operator..."
                    }
                  >
                    {operators.map((op) => (
                      <MenuItem key={op.value} value={op.value}>
                        {op.label.toUpperCase()}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ display: "flex", flex: 1, width: "100%", gap: 1 }}>
                <TextField
                  variant="outlined"
                  size="small"
                  value={clause.value}
                  onChange={(e) => updateFilterClause(clause.id, "value", e.target.value)}
                  label="Value"
                  InputLabelProps={{
                    shrink: false,
                    sx: {
                      display: clause.value ? "none" : "block",
                      fontSize: "12px",
                      color: "#aaa",
                    },
                  }}
                  sx={{
                    flex: 1,
                    backgroundColor: "#2E2E2E",
                    fontSize: "12px",
                    height: "30px",
                    "& .MuiOutlinedInput-root": {
                      height: "30px",
                      fontSize: "12px",
                      padding: "0 8px",
                      color: "#fff",
                    },
                    "& .MuiOutlinedInput-input": {
                      padding: 0,
                      display: "flex",
                      alignItems: "center",
                    },

                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#444",
                    },
                  }}
                />

                {/* delete icon button trash can*/}
                <IconButton
                  size="small"
                  sx={{
                    ml: 1,
                    color: "rgb(255, 111, 92)",

                    "&:hover": {
                      backgroundColor: "#444",
                    },
                  }}
                  onClick={() => removeFilterClause(clause.id)}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          ))}
          <Button
            variant="outlined"
            onClick={addFilterClause}
            sx={{
              mb: 2,
              mt: 2,
              width: "100%",
              backgroundColor: "#535456",
              color: "#fff",
              height: "30px",
              fontSize: "12px",
              border: "none",
            }}
          >
            Add Filter Clause
          </Button>
          <Divider sx={{ mb: 2, borderColor: "#444" }} />
          {error && (
            <Typography color="error" sx={{ mt: 2 }}>
              {error}
            </Typography>
          )}
        </>
      )}
    </Box>
  );
};
